import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "../context/AppContext";
import { pickReview, recordAnswer, getDueCount } from "../lib/store";
import { InteractiveQuestion } from "../components/InteractiveQuestion";
import { RefreshCw, ChevronRight, CalendarClock, CheckCircle2, Sparkles } from "lucide-react";

const SIZE = 20;

export default function Reviser() {
  const { level } = useApp();
  const dueCount = useMemo(() => getDueCount(level), [level]);
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState(false);

  const start = () => {
    const q = pickReview(level, SIZE);
    setQueue(q);
    setIdx(0);
    setAnswered(false);
    setCorrect(0);
    setDone(false);
  };

  const onAnswered = (ok, q) => {
    recordAnswer(level, q.id, ok);
    setAnswered(true);
    if (ok) setCorrect((c) => c + 1);
  };
  const next = () => {
    if (idx + 1 >= queue.length) setDone(true);
    else {
      setIdx((i) => i + 1);
      setAnswered(false);
    }
  };

  if (!queue) {
    return (
      <div className="max-w-2xl space-y-8">
        <div>
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary">
            <CalendarClock size={14} /> Répétition espacée
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Réviser</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            La révision espacée vous repropose les questions au bon moment : celles que vous ratez reviennent vite, celles que vous maîtrisez s'espacent dans le temps.
          </p>
        </div>
        <div className="rounded-sm border border-border bg-card p-6">
          <div className="flex items-center gap-4">
            <span className="grid place-items-center w-16 h-16 rounded-sm bg-primary/10 text-primary font-mono text-2xl font-bold">
              {dueCount}
            </span>
            <div>
              <div className="font-display font-bold text-lg">
                {dueCount > 0 ? "questions à réviser aujourd'hui" : "Rien d'urgent à réviser"}
              </div>
              <p className="text-sm text-muted-foreground">
                {dueCount > 0
                  ? "Nous compléterons avec vos points faibles si besoin."
                  : "Répondez à des questions (Quiz, Étudier, Examen) pour alimenter votre file de révision."}
              </p>
            </div>
          </div>
          <button
            onClick={start}
            data-testid="review-start"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-3.5 font-semibold hover:-translate-y-px transition-transform"
          >
            <Sparkles size={18} /> Démarrer la révision
          </button>
        </div>
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="max-w-lg mx-auto text-center space-y-5 py-10">
        <CheckCircle2 size={48} className="text-success mx-auto" />
        <h1 className="font-display font-black text-2xl">Aucune question à réviser</h1>
        <p className="text-muted-foreground text-sm">
          Commencez par répondre à des questions dans les autres modules ; elles apparaîtront ensuite ici au bon moment.
        </p>
        <Link to="/quiz" className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-6 py-3 font-semibold">
          Aller au Quiz <ChevronRight size={18} />
        </Link>
      </div>
    );
  }

  if (done) {
    const pct = Math.round((correct / queue.length) * 100);
    return (
      <div className="max-w-lg mx-auto text-center space-y-6 py-8">
        <div className="font-mono text-6xl font-bold tabular" style={{ color: pct >= 70 ? "hsl(var(--success))" : "hsl(var(--warning))" }}>
          {pct}%
        </div>
        <h1 className="font-display font-black text-2xl">{correct} / {queue.length} bonnes réponses</h1>
        <p className="text-sm text-muted-foreground">Vos échéances de révision ont été mises à jour.</p>
        <button onClick={() => setQueue(null)} data-testid="review-restart" className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-6 py-3 font-semibold hover:-translate-y-px transition-transform">
          <RefreshCw size={18} /> Terminer
        </button>
      </div>
    );
  }

  const q = queue[idx];
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => setQueue(null)} data-testid="review-quit" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          Quitter
        </button>
        <div className="flex items-center gap-4 font-mono text-sm">
          <span className="text-success">{correct} bonnes</span>
          <span className="text-muted-foreground">{idx + 1} / {queue.length}</span>
        </div>
      </div>
      <div className="h-1.5 rounded-sm bg-muted overflow-hidden">
        <div className="h-full bg-primary transition-[width]" style={{ width: `${(idx / queue.length) * 100}%` }} />
      </div>

      <InteractiveQuestion key={q.id} q={q} index={idx} onAnswered={onAnswered} testPrefix="review-q" />

      {answered && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end">
          <button onClick={next} data-testid="review-next" className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-5 py-2.5 font-semibold hover:-translate-y-px transition-transform">
            {idx + 1 >= queue.length ? "Terminer" : "Suivant"} <ChevronRight size={18} />
          </button>
        </motion.div>
      )}
    </div>
  );
}
